<template lang="pug">
el-dialog(
  :model-value="modelValue"
  :title="title"
  top="5vh"
  width="100%"
  class="el-dialog__max-w-700"
  @update:model-value="$emit('update:modelValue', $event)"
)
  .dialog-content
    div.search-controls.d-flex.align-items-center.mb-4
      el-input(
        v-model="searchQuery"
        placeholder="Search"
        prefix-icon="el-icon-search"
        @input="handleSearch"
        class="flex-grow-1 me-3"
      )
        template(#prefix='')
          img(
            src="@/assets/icons/light/magnify-light.svg"
            style="font-size: 20px;"
          )
      el-select(
        v-model="itemsPerPage"
        @change="handleItemsPerPageChange"
        style="width: 120px"
      )
        el-option(
          v-for="option in itemsPerPageOptions"
          :key="option"
          :label="`${option}`"
          :value="option"
        )

    .items-list.mb-4
      .list-group.list-group-flush
        .list-group-item(
          v-for="(item, index) in items"
          :key="item.id || index"
          class="py-3 border-bottom list-item"
          @click="onItemClick(item)"
        )
          .row.row-sm.align-items-center.flex-nowrap
            .col-auto
              el-checkbox(
                v-model="item.featured"
                @change="onCheckboxChange($event, item)"
                @click.stop
              )
            .col-auto
              img(
                :src="item.image"
                class="item-image size-50px obj-f-c rounded"
              )
            .col {{ item.title }}

    .d-flex.justify-content-between.align-items-center.mt-4(v-if="items.length > 0")
      .items-info Showing {{ items.length }} items
      .pagination-wrapper.d-flex.align-items-center
        button.page-btn.prev(
          @click="handleCurrentChange(currentPage - 1)"
          :disabled="currentPage === 1"
        ) Prev
        .page-numbers.mx-3 {{ currentPage }}
        button.page-btn.next(
          @click="handleCurrentChange(currentPage + 1)"
          :disabled="items.length < itemsPerPage"
        ) Next

  template(#footer)
    .dialog-footer
      el-button(@click="handleClose") Cancel
      el-button(
        type="success"
        v-loading="loading"
        @click="handleDone"
      ) Done
</template>

<script lang="ts">
import { ref, defineComponent, watch } from 'vue';
import { debounce } from 'lodash';

interface ListItem {
  id?: string | number;
  title: string;
  image: string;
  featured: boolean;
  order?: number;
}

export default defineComponent({
  name: 'DialogAddToFeaturedItems',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'SEARCH' },
    items: { type: Array as () => ListItem[], default: () => [] },
    featuredItems: { type: Array as () => ListItem[], default: () => [] },
    loading: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'close-dialog', 'save-item', 'page-change', 'items-per-page-change', 'search'],

  setup(props, { emit }) {
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const itemsPerPageOptions = [5, 10, 20];
    const selectedItems = ref<ListItem[]>([]);

    const handleSearch = debounce(() => {
      emit('search', searchQuery.value);
    }, 600);

    const handleItemsPerPageChange = (value) => {
      itemsPerPage.value = value;
      emit('items-per-page-change', value);
    };

    const handleCurrentChange = (page) => {
      currentPage.value = page;
      emit('page-change', page);
    };

    const onCheckboxChange = (event: boolean, listItem: ListItem) => {
      const item = { ...listItem };
      if (event) {
        if (props.featuredItems?.length) {
          const maxOrder = Math.max(...props.featuredItems.map((item: ListItem) => item.order || 0));
          item.order = maxOrder + 1;
        } else {
          item.order = 0;
        }
      }
      item.featured = event;
      
      if (event) {
        selectedItems.value.push(item);
      } else {
        selectedItems.value = selectedItems.value.filter(i => i.id !== item.id);
      }

      const itemIndex = props.items.findIndex(i => i.id === item.id);
      if (itemIndex !== -1) {
        props.items[itemIndex].featured = event;
      }
    };

    const onItemClick = (item: ListItem) => {
      const newValue = !item.featured;
      onCheckboxChange(newValue, item);
    };

    const handleDone = () => {
      selectedItems.value.forEach(item => {
        emit('save-item', item);
      });
      
      props.items.forEach(item => {
        item.featured = false;
      });

      searchQuery.value = '';
      
      emit('close-dialog');
    };

    watch(() => props.modelValue, (newValue) => {
      if (!newValue) {
        searchQuery.value = '';
        currentPage.value = 1;
        selectedItems.value = [];
        props.items.forEach(item => {
          item.featured = false;
        });
      } else {
        emit('search', '');
      }
    });

    return {
      searchQuery,
      currentPage,
      itemsPerPage,
      itemsPerPageOptions,
      handleClose: () => emit('close-dialog'),
      handleDone,
      onCheckboxChange,
      handleItemsPerPageChange,
      handleCurrentChange,
      handleSearch,
      onItemClick
    };
  },
});
</script>

<style lang="scss" scoped>
.dialog-content {
  padding: 0 24px;
}

.search-controls {
  .el-input {
    --el-input-bg-color: #f5f7fa;
    --el-input-hover-border-color: #1fbeaf;
    --el-input-focus-border-color: #1fbeaf;
  }

  .el-select {
    --el-select-border-color-hover: #1fbeaf;
    --el-select-input-focus-border-color: #1fbeaf;
  }
}

.list-group-item {
  &:last-child {
    border-bottom: none !important;
  }
}

.item-image {
  object-fit: cover;
  border-radius: 4px;
}

.items-info {
  color: #606266;
  font-size: 14px;
}

.pagination-wrapper {
  margin-top: 0;

  .page-btn {
    background: transparent;
    border: 1px solid #1fbeaf;
    border-radius: 4px;
    padding: 6px 16px;
    cursor: pointer;
    transition: all 0.3s;
    color: #1fbeaf;
    font-size: 14px;

    &:hover:not(:disabled) {
      background: #1fbeaf;
      color: white;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      border-color: #c0c4cc;
      color: #c0c4cc;
    }
  }

  .page-numbers {
    text-align: center;
    font-size: 14px;
    color: #606266;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .el-button {
    min-width: 100px;
    
    &--success {
      --el-button-bg-color: #1fbeaf;
      --el-button-border-color: #1fbeaf;
      --el-button-hover-bg-color: #19a799;
      --el-button-hover-border-color: #19a799;
    }
  }
}

:deep(.el-dialog__body) {
  padding: 24px 0;
}

:deep(.el-dialog__footer) {
  padding: 16px 24px;
  border-top: 1px solid #dcdfe6;
}

.list-item {
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f7fa;
  }
}
</style>
